import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FiAward, FiShield, FiCheckCircle, FiArrowUpRight } from "react-icons/fi";
import PageHeader from "../components/PageHeader";
import "./Certifications.css";

const certs = [
  {
    name: "FSSAI",
    full: "Food Safety and Standards Authority of India",
    body: "Central food-safety registration covering all our processing, packing, and storage facilities. Every shipment leaves under FSSAI compliance.",
    icon: FiShield,
    color: "#B8432A",
  },
  {
    name: "APEDA",
    full: "Agricultural & Processed Food Products Export Development Authority",
    body: "Registration with India's apex export body for agricultural products — ensuring export quality controls and buyer-side recognition.",
    icon: FiAward,
    color: "#D98B2B",
  },
  {
    name: "ISO 22000",
    full: "Food Safety Management",
    body: "International food safety management standard, covering hazard analysis, traceability, and control across the supply chain.",
    icon: FiCheckCircle,
    color: "#4A6B3A",
  },
  {
    name: "Spice Board of India",
    full: "Registered Exporter",
    body: "Licensed and registered with the Spice Board — the statutory body that regulates India's spice exports.",
    icon: FiAward,
    color: "#8B6F47",
  },
  {
    name: "IEC",
    full: "Importer–Exporter Code",
    body: "Active IEC issued by the Directorate General of Foreign Trade (DGFT), India — mandatory for all export transactions.",
    icon: FiShield,
    color: "#E2B13E",
  },
  {
    name: "GST Registered",
    full: "Goods and Services Tax",
    body: "Fully GST-compliant entity with all tax filings up to date — enabling clean, transparent invoicing for Indian and export buyers.",
    icon: FiCheckCircle,
    color: "#8B2E1C",
  },
];

const standards = [
  { label: "Pesticide residue compliance", detail: "EU MRL, FDA, Japan PLS" },
  { label: "Heavy metal & aflatoxin testing", detail: "NABL-accredited labs" },
  { label: "Microbiological testing", detail: "Salmonella, E. coli, yeast & mould" },
  { label: "Fumigation as per ISPM-15", detail: "Wooden packaging compliance" },
  { label: "Steam sterilisation available", detail: "For premium buyers on request" },
  { label: "Traceability", detail: "Lot-level, farm-to-container" },
];

function Certifications() {
  return (
    <motion.main
      className="page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageHeader
        eyebrow="Certifications & Compliance"
        title="The paperwork <em>behind the promise.</em>"
        subtitle="Certifications don't make a good exporter — but they are the minimum bar. Here's ours, along with the testing and compliance protocols we apply on every shipment."
      />

      <section className="section">
        <div className="container">
          <div className="certs-grid">
            {certs.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.name}
                  className="cert-card"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: i * 0.06 }}
                >
                  <div
                    className="cert-card__icon"
                    style={{ background: c.color }}
                  >
                    <Icon />
                  </div>
                  <div className="cert-card__name">
                    <h3>{c.name}</h3>
                    <span>{c.full}</span>
                  </div>
                  <p>{c.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section standards">
        <div className="container standards__inner">
          <div>
            <span className="eyebrow">Testing & protocols</span>
            <h2>
              What we test for, <br />
              <em>on every batch.</em>
            </h2>
            <p>
              Every lot intended for export goes through a standard panel of
              tests before shipment. Buyer-specific protocols — EU MRLs, Japan
              PLS, US FDA, GCC — are added on top when required.
            </p>
          </div>

          <div className="standards__list">
            {standards.map((s, i) => (
              <motion.div
                key={s.label}
                className="standards__item"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <FiCheckCircle />
                <div>
                  <strong>{s.label}</strong>
                  <span>{s.detail}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-band">
        <div className="container cta-band__inner">
          <h2>
            Need a specific certificate or test report? <br />
            <em>We'll arrange it.</em>
          </h2>
          <Link to="/contact" className="btn btn-primary">
            Request documentation <FiArrowUpRight />
          </Link>
        </div>
      </section>
    </motion.main>
  );
}

export default Certifications;
